import React, { useState } from "react";
import { useParams } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import {
  EvidenceBlock,
  JobFacts,
  JobHeader,
  KIND_LABEL,
  PtError,
  PtLoading,
  PtPage,
  ScopeBlock,
  StateBox,
  StatusPill,
  fmtDateTime,
  money,
  parseAmount,
  postPublic,
  textOf,
  usePublicTurnover,
} from "./turnoverShared";

/**
 * The form a vendor fills in from the email link. `onSent` receives the quote that was
 * accepted; `onStale` is called when the server says the job moved on (409) or the link
 * died (401/404), and the parent re-reads the job instead of this form guessing.
 */
const QuoteForm = ({ url, onSent, onStale }) => {
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const n = parseAmount(amount);
    if (n === null) {
      setError("Enter the amount you are quoting, in Canadian dollars.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await postPublic(url, { amount: n, notes: notes.trim() });
      const q = (res && res.data && res.data.quote) || {};
      onSent({
        amount: q.amount !== undefined && q.amount !== null ? q.amount : n,
        notes: notes.trim(),
        submitted_at: q.submitted_at || new Date().toISOString(),
      });
    } catch (err) {
      if (err.status === 409 || err.status === 401 || err.status === 404) {
        onStale(err);
        return;
      }
      setError(err.message || "That did not go through. Please try again.");
      setBusy(false);
    }
  };

  return (
    <form className="pt-form" onSubmit={submit} noValidate>
      <div className="pt-form-grid">
        <div>
          <label className="form-label" htmlFor="pt-amount">
            Your estimate (CAD)
          </label>
          <div className="input-group">
            <span className="input-group-text">$</span>
            <input
              id="pt-amount"
              className="form-control"
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              autoFocus
            />
          </div>
        </div>
        <div>
          <label className="form-label" htmlFor="pt-notes">
            Notes for the property <span className="pt-optional">(optional)</span>
          </label>
          <textarea
            id="pt-notes"
            className="form-control"
            rows={3}
            placeholder="What the price covers, lead time, anything you need from the property"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>
      {error ? <p className="pt-form-error">{error}</p> : null}
      <div className="pt-actions">
        <button type="submit" className="btn btn-primary" disabled={busy}>
          <FeatherIcon icon="send" size={15} />
          {busy ? "Sending…" : "Send my estimate"}
        </button>
      </div>
      <p className="pt-hint">The property reviews every estimate before any work is scheduled.</p>
    </form>
  );
};

const QuoteOutcome = ({ job, sent }) => {
  const q = job.quote || {};
  const a = job.approval || {};

  if (sent) {
    return (
      <StateBox tone="success" icon="check-circle" title="Thank you, your estimate has been sent for approval">
        <p>
          <strong>{money(sent.amount)}</strong>
          {sent.notes ? ` · ${sent.notes}` : ""}
        </p>
        <p className="pt-hint">Sent {fmtDateTime(sent.submitted_at)}. The property will let you know once they decide.</p>
      </StateBox>
    );
  }

  switch (job.status) {
    case "quoted":
      return (
        <StateBox
          tone="info"
          icon="clock"
          title={`You quoted ${money(q.amount)}${q.submitted_at ? ` on ${fmtDateTime(q.submitted_at)}` : ""}`}
        >
          <p>It is pending approval by the property.</p>
          {q.notes ? <p className="pt-hint">Your notes: {q.notes}</p> : null}
        </StateBox>
      );
    case "approved":
      return (
        <StateBox
          tone="success"
          icon="check-circle"
          title={`Approved at ${money(a.amount !== undefined && a.amount !== null ? a.amount : q.amount)}`}
        >
          <p>
            {a.at ? `Approved ${fmtDateTime(a.at)}` : "Approved"}
            {a.by_name ? ` by ${a.by_name}` : ""}. The property will be in touch to schedule the work.
          </p>
          {a.note ? <p className="pt-hint">Note from the property: {a.note}</p> : null}
        </StateBox>
      );
    case "declined":
      return (
        <StateBox tone="danger" icon="x-circle" title="Your estimate was declined">
          <p>{(job.declined && job.declined.reason) || "The property decided not to go ahead with this estimate."}</p>
          {q.amount !== undefined && q.amount !== null ? (
            <p className="pt-hint">
              You quoted {money(q.amount)}
              {q.submitted_at ? ` on ${fmtDateTime(q.submitted_at)}` : ""}.
            </p>
          ) : null}
        </StateBox>
      );
    case "cancelled":
      return (
        <StateBox tone="muted" icon="slash" title="This request was cancelled">
          <p>{(job.cancelled && job.cancelled.reason) || "The property withdrew this request. No estimate is needed."}</p>
        </StateBox>
      );
    default:
      return null;
  }
};

/** /turnover/quote/:id/:aid/:token - the vendor's estimate request, no login. */
const TurnoverQuotePage = () => {
  const { id, aid, token } = useParams();
  const { loading, error, data: job, reload, url } = usePublicTurnover("quote", id, aid, token);
  const [sent, setSent] = useState(null);
  const [staleMessage, setStaleMessage] = useState("");

  const handleStale = (err) => {
    setStaleMessage(err.status === 409 ? err.message : "");
    reload();
  };

  let body;
  if (loading) {
    body = <PtLoading text="Loading the estimate request…" />;
  } else if (error) {
    body = <PtError error={error} />;
  } else {
    const kind = KIND_LABEL[job.kind] || "Turnover";
    const company = textOf(job.company);
    body = (
      <>
        <JobHeader job={job} eyebrow={`${kind} turnover${company ? ` · ${company}` : ""}`} title="Estimate request">
          <div className="pt-status-row">
            <StatusPill status={sent ? "quoted" : job.status} />
            {job.vendor_name ? <span className="pt-meta">For {job.vendor_name}</span> : null}
          </div>
        </JobHeader>

        <JobFacts job={job} />
        <ScopeBlock scope={job.scope} />
        <EvidenceBlock job={job} />

        <section className="pt-section">
          <h2 className="pt-section-title">
            <FeatherIcon icon="dollar-sign" size={16} />
            Your estimate
          </h2>
          {staleMessage ? (
            <StateBox tone="warning" icon="info" title="This request has changed since the page was opened">
              <p>{staleMessage}</p>
            </StateBox>
          ) : null}
          {!sent && job.status === "assigned" ? (
            <QuoteForm url={url} onSent={setSent} onStale={handleStale} />
          ) : (
            <QuoteOutcome job={job} sent={sent} />
          )}
        </section>
      </>
    );
  }

  return <PtPage footer="This link is personal to you. Please do not forward it.">{body}</PtPage>;
};

export { TurnoverQuotePage };
export default TurnoverQuotePage;
